import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import styled from 'astroturf/react';

import { currentUser } from 'js/utils/user';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { fsEvent } from 'js/fullstory';
import {
  FollowableUser,
  isPaper,
  News,
  AiButtonType,
} from 'js/social/shared/types';
import ViewsWatcher from 'js/social/shared/ViewsWatcher';
import {
  getImpressionPaperType,
  getImpressionFeedExtraData,
  getImpressionPage,
  ImpressionPropsGenerator,
  makeImpressionPropsGenerator,
} from 'js/social/ImpressionHelper';
import { legacyRecordNewsfeedView } from 'js/social/api/legacy_views_reporting';
import { createNewsView } from 'js/social/api/viewed_news_api';
import { FeedContext } from 'js/social/contexts/feed';
import { FeedPage } from 'js/social/Feed/hooks/useFeedConfig';
import { FeedCommentsActions } from 'js/social/Comment/useCommentInteractions';
import { Reason as ReasonBase } from 'js/social/shared/Reason';
import RatingCard from 'js/social/RatingCard/RatingCard';

import Base from './Base';
import { NewsInteractionBar } from './NewsInteractionBar';
import useNewsInteractions, {
  FeedPostsActions,
} from '../../News/useNewsInteractions';
import PaperInteractions from '../PaperInteractions';
import PaperNewsStory from '../PaperNewsStory';
import MultiPaperNewsStory from '../MultiPaperNewsStory';
import PaperImages from '../PaperImages';
import PaperAbstract from '../PaperAbstract';
import { PostImage } from 'js/social/shared/PostImage';
import { useAiSummary } from './useAiSummary';
import { AiSummary } from './AiSummary';
import {
  CreateComment,
  CommentReplyTo,
} from '../CreateSocialItem/CreateComment';
import useCurrentUserAsAuthor from 'js/social/shared/CreateSocialItem/useCurrentUserAsAuthor';
import { Comments } from './Comments';
import { NewsHeader } from './NewsHeader';
import { useReader } from '../Reader/ReaderProvider';
import { triggerFocusFromClick } from '../CreateSocialItem/triggerFocusFromClick';
import { ResearcherList } from './ResearcherList';
import { PostBody } from 'js/social/News/Body';

const Reason = styled(ReasonBase)`
  padding: var(--spacing-xs-16, 16px);

  color: var(--text-primary, #141413);
  border-bottom: 1px solid var(--stroke-light, #dddde2);

  @import 'stylesheets/design_system/font.scss';
  @include body__small;
  line-height: var(--type-body-sm-line-height, 22.4px); /* 160% */
`;

const CommentsWrapper = styled.div<{ variant?: 'default' | 'dense' }>`
  padding-top: 16px;
  margin: 0 16px;
  border-top: 1px solid var(--stroke-light, #dddde2);

  &.variant-dense {
    border-top: none;
  }
`;

const ContentWrapper = styled.div`
  padding: 0 var(--spacing-xs-16) var(--spacing-xs-16) var(--spacing-xs-16);
`;

type Props = News &
  FeedPostsActions &
  FeedCommentsActions & {
    /**
     * Each post only has knowledge of user ids, e.g. post owner,
     * commenter.
     * Information about users exists separately and is referenced by not
     * just a single post. This function lets a post access a particular
     * user's info.
     *
     * @param id ID of a user
     * @return A hash containing user data
     */
    getUserData: (id: number) => FollowableUser;
    index: number;
  };

type GetImpressionProps = {
  feedConfig: {
    page: FeedPage;
    extendedOnsiteRecsTestBucket: string;
    recencyWeightedTestBucket: string;
    guessTheResearcherTestBucket: string;
  };
  props: Props;
  hasImages: boolean;
};

const getImpressionPropsGenerator = ({
  feedConfig,
  props,
  hasImages,
}: GetImpressionProps): ImpressionPropsGenerator => {
  const recommendationMetrics =
    props.newsType === 'work' || props.newsType === 'openalex_work'
      ? props.recommendationMetrics
      : undefined;
  const recommendationScore = props.recommendationScore ?? undefined;
  const impressionTrackDimensions = true;

  const impressionExtraData = getImpressionFeedExtraData({
    newsType: props.newsType,
    newsId: props.newsId,
    feedIndex: props.index,
    feedReason: props.reason.type,
    recommendationScore,
    recommendationMetrics,
    extendedOnsiteRecsTestBucket: feedConfig.extendedOnsiteRecsTestBucket,
    recencyWeightedTestBucket: feedConfig.recencyWeightedTestBucket,
    guessTheResearcherTestBucket: feedConfig.guessTheResearcherTestBucket,
    newsStory: props.newsStory,
    topicGroupId:
      props.newsType === 'work' ? props.autogroupData?.autogroupId : undefined,
    topicGroupName:
      props.newsType === 'work'
        ? props.autogroupData?.autogroupTitle
        : undefined,
    topicGroupSources:
      props.newsType === 'work' && props.autogroupData?.autogroupSource
        ? [props.autogroupData?.autogroupSource]
        : undefined,
    topicGroupActorCount:
      props.newsType === 'work'
        ? props.autogroupData?.relevantUserIds.length
        : undefined,
    impression_tracking_id: props.impression_tracking_id,
    imageId: props.newsType === 'post' ? props.image?.id : undefined,
    videoId: props.newsType === 'post' ? props.video?.id : undefined,
  });

  const { work, newsStory } = props;
  const [impressionElement, impressionEntityId, impressionEntityType] = work
    ? isPaper(work)
      ? ['paper', work.id, ImpressionEntity.Work]
      : ['oa-work', work.id, ImpressionEntity.OpenalexWork]
    : props.newsType === 'paper_set'
      ? ['paper-set', props.newsId, ImpressionEntity.PaperSet]
      : ['post', 0, ImpressionEntity.UnMapped];

  const impressionPage = getImpressionPage(feedConfig.page);
  const impressionSection = `feed-${props.index}`;
  const impressionPaperType = hasImages
    ? 'images'
    : newsStory
      ? 'llm-news-story'
      : getImpressionPaperType(props.paperType);

  return makeImpressionPropsGenerator(
    impressionPage,
    impressionSection,
    impressionElement,
    impressionPaperType,
    impressionEntityType,
    impressionEntityId,
    impressionExtraData,
    impressionTrackDimensions,
  );
};

const NewsComponent: React.FunctionComponent<Props> = (props: Props) => {
  const {
    newsId,
    newsType,
    content,
    tags,
    work,
    newsStory,
    userId,
    getUserData,
    looksDeleted,
    hoisted,
    createdAt = Date.now().toString(),
    reactions,
    comments,
  } = props;
  const { actions, permissions } = useNewsInteractions({
    ...props,
    messageText: {
      bookmarkSuccess: 'Saved to library',
      bookmarkFailure: 'Failed to save to library',
      unbookmarkSuccess: 'Removed from library',
      unbookmarkFailure: 'Failed to remove from library',
    },
  });
  const {
    recordViews,
    useReporting,
    feedConfig,
    showInlineReader,
    showAiButtons,
    showSocialActions,
    showDenseDesktop,
    showRatings,
  } = useContext(FeedContext);
  const [currentImage, setCurrentImage] = useState(0);

  const [showAddComment, setShowAddComment] = useState(false);
  const author = useCurrentUserAsAuthor();

  const hasImages = (work?.images || []).length !== 0;
  const hasReader = !!(showInlineReader && work?.attachment?.readable);

  // AI summary related logic
  let aiButton: AiButtonType | undefined = undefined;
  let aiExtra: Record<string, unknown> = {};
  if (showAiButtons) {
    if (newsStory) {
      if (newsStory.showAiButton) {
        if (newsStory.type === 'key_finding') {
          aiButton = 'finding';
          aiExtra = { storyId: newsStory.id };
        }
      }
    } else if (hasImages) {
      aiButton = 'figure';
      aiExtra = { imageId: work?.images?.[currentImage]?.id };
    }
  }
  const {
    summary,
    loading: summaryLoading,
    fetchSummary: handleExplain,
    clearSummary,
  } = useAiSummary(newsId, newsType, aiButton, aiExtra);

  const impressionPropsGen = useMemo(
    () => getImpressionPropsGenerator({ feedConfig, props, hasImages }),
    [feedConfig, props, hasImages],
  );

  const { showReader } = useReader();
  const showReaderForWork = useCallback(
    () =>
      work &&
      showReader({
        newsType,
        newsId,
        work,
        impressionPropsGen,
      }),
    [showReader, impressionPropsGen, newsType, newsId, work],
  );

  const handleShowCommentModal = useCallback(() => {
    triggerFocusFromClick();
    setShowAddComment(true);
  }, []);

  const trackImpressions = ['home', 'research_interest'].includes(
    feedConfig.page,
  );

  const recordPostView = () => {
    if (currentUser != null) {
      if (useReporting && userId && createdAt) {
        legacyRecordNewsfeedView(currentUser.id, newsType, {
          id: newsId,
          userId,
          createdAt,
          index: props.index,
        });
        fsEvent('UserViewedSocial', {
          newsId,
          newsType,
          numCommentsOnView: props.comments.count,
          numReactionsOnView: props.reactions.like.count,
        });
      }

      if (recordViews) {
        const key = {
          post: 'postId',
          paper_update: 'paperUpdateId',
          paper_highlight: 'workId',
          work: 'workId',
          openalex_work: 'openalexWorkId',
          paper_set: 'paperSetId',
        }[newsType];

        if (key) {
          return createNewsView({
            userId: currentUser.id,
            [key]: newsId,
            numCommentsOnView: props.comments.count,
            numReactionsOnView: props.reactions.like.count,
          }).then(() => {});
        } else {
          return Promise.resolve();
        }
      }
    }
    return Promise.resolve();
  };

  const paperInteractions = work && !showDenseDesktop && (
    <PaperInteractions
      work={work}
      showBookmark={false}
      aiButton={aiButton}
      canUseReader={hasReader}
      showReader={showReaderForWork}
      isBookmarked={!!permissions.isBookmarked}
      onBookmark={actions.bookmarkWork}
      onUnbookmark={actions.unbookmarkWork}
      onExplain={handleExplain}
      impressionPropsGen={impressionPropsGen()()}
    />
  );

  const showContent =
    props.content !== null && (content.length > 0 || tags.length > 0);

  const paperComponent: React.ReactNode = !looksDeleted && (
    <ViewsWatcher
      key={`${newsType}:${newsId}:${content}:${tags}`}
      type={newsType}
      id={newsId}
      sendView={recordPostView}
      threshold={0.8}
    >
      <ImpressionWatcher
        active={trackImpressions}
        {...impressionPropsGen()()()}
      >
        {showContent && (
          <ContentWrapper>
            <PostBody
              content={content}
              tags={tags}
              hashtags={props.hashtags}
              // Allow all hashtags for rate-limited posts
              allowAllHashtags={newsId === 0}
              isQuotedNews={props.isQuotedNews}
              isSinglePostPage={false}
              isTextOnly={
                newsType === 'post' &&
                !props.isQuotedNews &&
                !props.quotable &&
                !props.image &&
                !props.work
              }
            />
          </ContentWrapper>
        )}

        {newsType === 'post' && props.image ? (
          <PostImage
            noBorder
            title={getUserData(props.userId)?.firstName}
            caption={content}
            image={props.image}
          />
        ) : newsStory && work ? (
          <PaperNewsStory
            newsStory={newsStory}
            work={work}
            canUseReader={hasReader}
            showReader={showReaderForWork}
            impressionPropsGen={impressionPropsGen()()}
            paperInteractions={paperInteractions}
            variant={showDenseDesktop ? 'dense' : 'default'}
          />
        ) : hasImages && work ? (
          <PaperImages
            work={work}
            canUseReader={hasReader}
            showReader={showReaderForWork}
            impressionPropsGen={impressionPropsGen()()}
            paperInteractions={paperInteractions}
            variant={showDenseDesktop ? 'dense' : 'default'}
            onImageChange={(imageIndex) => {
              clearSummary();
              setCurrentImage(imageIndex);
            }}
          />
        ) : work ? (
          <PaperAbstract
            work={work}
            canUseReader={hasReader}
            showReader={showReaderForWork}
            showSummary
            impressionPropsGen={impressionPropsGen()()}
            paperInteractions={paperInteractions}
            variant={showDenseDesktop ? 'dense' : 'default'}
          />
        ) : null}
        {aiButton && (summaryLoading || summary) && (
          <AiSummary
            type={aiButton}
            loading={summaryLoading}
            summary={summary}
          />
        )}
      </ImpressionWatcher>
    </ViewsWatcher>
  );

  const paperSetComponent: React.ReactNode =
    newsType === 'paper_set' ? (
      <ViewsWatcher
        key={`${newsType}:${newsId}:${content}:${tags}`}
        type={newsType}
        id={newsId}
        sendView={recordPostView}
        threshold={0.8}
      >
        <ImpressionWatcher
          active={trackImpressions}
          {...impressionPropsGen()()()}
        >
          {newsStory && (
            <MultiPaperNewsStory
              newsStory={newsStory}
              impressionPropsGen={impressionPropsGen}
              props={props}
            />
          )}
        </ImpressionWatcher>
      </ViewsWatcher>
    ) : null;

  let headerComponent: ReactNode;
  let replyToData: CommentReplyTo | null = null;
  if (showSocialActions) {
    if (
      newsType === 'work' &&
      props.reason.type === 'autogroup_enhanced' &&
      props.autogroupData
    ) {
      replyToData = {
        kind: 'recommendation',
        recommendation: {
          name: 'Academia recommendations',
          subtitle: 'Recommended based on your interests',
        },
      };

      const relevantUsers =
        props.autogroupData?.relevantUserIds.map(getUserData);
      headerComponent = (
        <NewsHeader
          type="autogroup_multi_user_recommended"
          relevantUsers={relevantUsers}
          autoGroupTitle={props.autogroupData?.autogroupTitle}
          action={props.autogroupData?.action}
          tooltipContents={
            <ResearcherList
              users={relevantUsers}
              newsId={newsId}
              newsType={newsType}
              reason={props.reason}
              impressionPropsGen={impressionPropsGen}
            />
          }
          impressionPropsGen={impressionPropsGen}
          timestamp={createdAt}
          variant={showDenseDesktop ? 'dense' : 'default'}
        />
      );
    } else if (newsType === 'post') {
      replyToData = {
        kind: 'recommendation',
        recommendation: {
          name: 'Academia recommendations',
          subtitle: 'Recommended based on your interests',
        },
      };

      headerComponent = (
        <NewsHeader
          type="post"
          author={getUserData(props.userId)}
          impressionPropsGen={impressionPropsGen}
          timestamp={createdAt}
          variant={showDenseDesktop ? 'dense' : 'default'}
        />
      );
    } else {
      replyToData = {
        kind: 'recommendation',
        recommendation: {
          name: 'Academia recommendations',
          subtitle: 'Recommended based on your interests',
        },
      };

      headerComponent = (
        <NewsHeader
          type="recommended"
          impressionPropsGen={impressionPropsGen}
          timestamp={createdAt}
          variant={showDenseDesktop ? 'dense' : 'default'}
        />
      );
    }
  } else {
    headerComponent = (
      <Reason
        type={props.reason.type || 'recommended'}
        users={(props.reason.data?.sourceUserIds || []).map(getUserData)}
        totalUsers={props.reason.data?.count ?? 0}
      />
    );
  }

  return (
    <>
      <Base hoisted={hoisted} variant={showDenseDesktop ? 'dense' : 'default'}>
        {headerComponent}
        {paperComponent}
        {paperSetComponent}
        {showRatings && (
          <RatingCard
            newsType={newsType}
            newsId={newsId}
            reason={props.reason.type || ''}
            variant="corex"
          />
        )}
        {showSocialActions && (
          <>
            <NewsInteractionBar
              isLiked={reactions.like.reacted}
              numLikes={reactions.like.count}
              onLike={actions.likeNews}
              onUnlike={actions.unlikeNews}
              showBookmark={newsType !== 'post'}
              isBookmarkEnabled={newsType !== 'paper_set'}
              isBookmarked={!!permissions.isBookmarked}
              numBookmarks={
                work?.bookmarkCount ?? (permissions.isBookmarked ? 1 : 0)
              }
              onBookmark={actions.bookmarkWork}
              onUnbookmark={actions.unbookmarkWork}
              numComments={props.comments.count}
              onAddComment={handleShowCommentModal}
              showComment={!props.commentsDisabled}
              showLike={
                !props.reactionsDisabled || props.reactions.like.reacted
              }
              impressionPropsGen={impressionPropsGen()()}
              variant={showDenseDesktop ? 'dense' : 'default'}
            />
            {comments.count > 0 && (
              <CommentsWrapper variant={showDenseDesktop ? 'dense' : 'default'}>
                <Comments
                  newsType={newsType}
                  newsId={newsId}
                  comments={comments.data}
                  commentsDisabled={!!props.commentsDisabled}
                  getAuthor={(comment) => getUserData(comment.userId)}
                  impressionPropsGen={impressionPropsGen}
                />
              </CommentsWrapper>
            )}
          </>
        )}
      </Base>
      {showAddComment && replyToData && (
        <CreateComment
          newsId={newsId}
          author={author}
          replyTo={replyToData}
          replyToContent={content ? { content, tags } : undefined}
          replyToWork={work}
          onClose={() => setShowAddComment(false)}
          onSubmit={actions.createComment}
          impressionPropsGen={impressionPropsGen}
        />
      )}
    </>
  );
};

export default NewsComponent;
